import { createFileRoute, redirect, useSearch } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { RootLayout } from '@/components/layout/RootLayout'
import { ChatWindow } from '@/components/chat/ChatWindow'
import { NeuralCanvas } from '@/components/landing/NeuralCanvas'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassButton } from '@/components/ui/GlassButton'
import { GlassInput, GlassTextarea } from '@/components/ui/GlassInput'
import { AgentStatusBadge } from '@/components/ui/GlassBadge'
import { agentQueryOptions, providersQueryOptions, channelEndpointsQueryOptions, channelBindingsQueryOptions, offeringPacksQueryOptions } from '@/lib/queryOptions'
import { api } from '@/lib/api'
import { MessageSquare, Settings2, ChevronLeft, Link2, Sparkles } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { z } from 'zod'
import { useT } from '@/i18n/provider'

export const Route = createFileRoute('/dashboard/agents/$agentId')({
  validateSearch: z.object({ tab: z.enum(['chat', 'settings']).optional() }),
  beforeLoad: async ({ context: { queryClient }, params }) => {
    await queryClient.ensureQueryData(agentQueryOptions(params.agentId)).catch(() => {
      throw redirect({ to: '/dashboard/agents' })
    })
  },
  component: AgentDetailPage,
})

function AgentDetailPage() {
  const t = useT()
  const { agentId } = Route.useParams()
  const { tab = 'chat' } = useSearch({ from: '/dashboard/agents/$agentId' })
  const navigate = Route.useNavigate()
  const qc = useQueryClient()

  const { data: agent } = useQuery(agentQueryOptions(agentId))
  const { data: providers = [] } = useQuery(providersQueryOptions())
  const { data: endpoints = [] } = useQuery(channelEndpointsQueryOptions())
  const { data: bindings = [] } = useQuery(channelBindingsQueryOptions(agentId))
  const { data: offeringPacks = [] } = useQuery(offeringPacksQueryOptions())

  const [name, setName] = useState(agent?.name ?? '')
  const [description, setDescription] = useState(agent?.description ?? '')
  const [systemPrompt, setSystemPrompt] = useState(agent?.systemPrompt ?? '')
  const [aiConfigId, setAiConfigId] = useState(agent?.aiConfigId ?? '')
  const [endpointId, setEndpointId] = useState('')
  const [offeringPackId, setOfferingPackId] = useState(String((agent?.metadata?.offeringPackId as string | undefined) ?? ''))

  useEffect(() => {
    if (!agent) return
    setName(agent.name)
    setDescription(agent.description ?? '')
    setSystemPrompt(agent.systemPrompt ?? '')
    setAiConfigId(agent.aiConfigId ?? '')
    setOfferingPackId(String((agent.metadata?.offeringPackId as string | undefined) ?? ''))
  }, [agent])

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.agents.update>[1]) =>
      api.agents.update(agentId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  })

  const statusMutation = useMutation({
    mutationFn: (status: Parameters<typeof api.agents.updateStatus>[1]) =>
      api.agents.updateStatus(agentId, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents', agentId] }),
  })

  const bindingMutation = useMutation({
    mutationFn: api.channels.bindings.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', 'bindings', agentId] }),
  })

  const removeBindingMutation = useMutation({
    mutationFn: api.channels.bindings.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels', 'bindings', agentId] }),
  })

  const applyOfferingMutation = useMutation({
    mutationFn: api.offeringPacks.apply,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agents', agentId] })
      qc.invalidateQueries({ queryKey: ['agents'] })
    },
  })

  if (!agent) return null

  const statusKey = {
    ACTIVE: 'status.ACTIVE',
    DRAFT: 'status.DRAFT',
    PAUSED: 'status.PAUSED',
    ARCHIVED: 'status.ARCHIVED',
  } as const

  const localizedStatus = t(statusKey[agent.status] ?? 'status.DRAFT')

  return (
    <RootLayout>
      <div className="relative min-h-full">
        {tab === 'chat' && (
          <div className="fixed inset-0 -z-10 pointer-events-none overflow-hidden" aria-hidden="true">
            <div className="absolute inset-0 bot-mesh-base" />
            <NeuralCanvas
              className="pointer-events-none opacity-95"
              speedMultiplier={2}
              motionMultiplier={1.25}
            />
            <div className="absolute inset-0 bot-mesh-drift" />
            <div className="absolute inset-0 bot-mesh-vignette" />
            <div className="absolute inset-0 bot-mesh-noise" />
          </div>
        )}

        <div className="relative z-10 flex flex-col h-full space-y-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/dashboard/agents" className="text-white/40 hover:text-white transition-colors">
            <ChevronLeft size={20} />
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-white">{agent.name}</h1>
              <AgentStatusBadge status={agent.status} />
            </div>
            {agent.aiConfig && (
              <p className="text-xs text-white/40 mt-0.5">
                {agent.aiConfig.providerId} · {agent.aiConfig.modelId}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {agent.status !== 'ACTIVE' && (
              <GlassButton size="sm" variant="secondary" onClick={() => statusMutation.mutate('ACTIVE')}>
                {t('agents.detail.activate')}
              </GlassButton>
            )}
            {agent.status === 'ACTIVE' && (
              <GlassButton size="sm" variant="ghost" onClick={() => statusMutation.mutate('PAUSED')}>
                {t('agents.detail.pause')}
              </GlassButton>
            )}
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 glass-sm p-1 w-fit">
          {([
            { id: 'chat' as const, label: t('agents.detail.tabChat'), icon: MessageSquare },
            { id: 'settings' as const, label: t('agents.detail.tabSettings'), icon: Settings2 },
          ]).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => navigate({ search: { tab: id } })}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition-all ${
                tab === id
                  ? 'bg-[var(--oc-accent)]/20 text-white border border-[var(--oc-accent)]/30'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'chat' && (
          <GlassCard className="flex-1 flex flex-col min-h-0" style={{ height: 'calc(100vh - 280px)' }}>
            {agent.status === 'ACTIVE' ? (
              <ChatWindow agentId={agentId} agentName={agent.name} />
            ) : (
              <div className="flex-1 flex items-center justify-center text-center space-y-3">
                <div>
                  <p className="text-white/40">{t('agents.detail.statusLabel', { status: localizedStatus.toLowerCase() })}</p>
                  <p className="text-sm text-white/25 mt-1">{t('agents.detail.activateToChat')}</p>
                </div>
              </div>
            )}
          </GlassCard>
        )}

        {tab === 'settings' && (
          <GlassCard className="space-y-5 max-w-2xl">
            <h2 className="font-semibold text-white">{t('agents.detail.settingsTitle')}</h2>
            <GlassInput label={t('agents.detail.nameLabel')} value={name} onChange={(e) => setName(e.target.value)} />
            <GlassInput label={t('agents.detail.descriptionLabel')} value={description} onChange={(e) => setDescription(e.target.value)} />
            <GlassTextarea
              label={t('agents.detail.systemPromptLabel')}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={6}
              hint={t('agents.detail.systemPromptHint')}
            />
            <div>
              <label className="text-sm font-medium text-white/70 block mb-1.5">{t('agents.detail.providerLabel')}</label>
              <select
                value={aiConfigId}
                onChange={(e) => setAiConfigId(e.target.value)}
                className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              >
                <option value="">{t('agents.detail.noneOption')}</option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id} style={{ background: '#1a1a2e' }}>
                    {p.name} ({p.modelId})
                  </option>
                ))}
              </select>
            </div>
            <GlassButton
              onClick={() => updateMutation.mutate({ name, description, systemPrompt, aiConfigId: aiConfigId || undefined })}
              loading={updateMutation.isPending}
            >
              {t('agents.detail.saveChanges')}
            </GlassButton>

            <div className="pt-3 border-t border-white/10 space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Sparkles size={14} />
                {t('agents.detail.offeringPackTitle')}
              </h3>
              <div className="flex flex-col md:flex-row gap-2">
                <select
                  value={offeringPackId}
                  onChange={(e) => setOfferingPackId(e.target.value)}
                  className="glass flex-1 px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
                >
                  <option value="">{t('agents.detail.offeringPackPlaceholder')}</option>
                  {offeringPacks.map((pack) => (
                    <option key={pack.id} value={pack.id} style={{ background: '#1a1a2e' }}>
                      {pack.name} ({pack.audience} · {pack.vertical})
                    </option>
                  ))}
                </select>
                <GlassButton
                  size="sm"
                  disabled={!offeringPackId}
                  loading={applyOfferingMutation.isPending}
                  onClick={() => applyOfferingMutation.mutate({ agentId, offeringPackId })}
                >
                  {t('agents.detail.applyOfferingPack')}
                </GlassButton>
              </div>
            </div>

            <div className="pt-3 border-t border-white/10 space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Link2 size={14} />
                {t('agents.detail.channelBindingsTitle')}
              </h3>

              <div className="flex flex-col md:flex-row gap-2">
                <select
                  value={endpointId}
                  onChange={(e) => setEndpointId(e.target.value)}
                  className="glass flex-1 px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
                >
                  <option value="">{t('agents.detail.channelBindingsPlaceholder')}</option>
                  {endpoints.map((endpoint) => (
                    <option key={endpoint.id} value={endpoint.id} style={{ background: '#1a1a2e' }}>
                      {endpoint.name} ({endpoint.channelType})
                    </option>
                  ))}
                </select>
                <GlassButton
                  size="sm"
                  disabled={!endpointId}
                  loading={bindingMutation.isPending}
                  onClick={() => bindingMutation.mutate({ agentId, endpointId, isDefault: bindings.length === 0 })}
                >
                  {t('agents.detail.bindChannelButton')}
                </GlassButton>
              </div>

              <div className="space-y-2">
                {bindings.length === 0 ? (
                  <p className="text-xs text-white/40">{t('agents.detail.noChannelBindings')}</p>
                ) : (
                  bindings.map((binding) => (
                    <div key={binding.id} className="glass-sm px-3 py-2 flex items-center justify-between">
                      <p className="text-xs text-white/70">
                        {binding.channelEndpoint.name} ({binding.channelEndpoint.channelType})
                        {binding.isDefault ? ` · ${t('agents.detail.defaultBinding')}` : ''}
                      </p>
                      <button
                        onClick={() => removeBindingMutation.mutate(binding.id)}
                        className="text-xs text-red-300 hover:text-red-200"
                      >
                        {t('agents.detail.removeBinding')}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </GlassCard>
        )}
      </div>
      </div>
    </RootLayout>
  )
}
